import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FishAvatar from '../../components/FishAvatar';
import Loading from '../../components/Loading';
import { fetchClassDecks, type Card } from '../../lib/cards';
import { burst } from '../../lib/confetti';
import { today, weekStartOf } from '../../lib/dates';
import { awardSandDollars } from '../../lib/ocean';
import { pointsFor, quizLink, quizTone, TILES, type QuizQuestion, type QuizState, type Ranked } from '../../lib/quiz';
import { grammarQuestions, mixQuestions, sharedVocab, storyPhrases, storyQuestions } from '../../lib/quizBuild';
import { supabase } from '../../lib/supabase';
import { isOpen } from '../../lib/kinds';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// The class game, hosted by the teacher (put it on the projector or share it
// in the meeting). Questions come from this week's work (lib/quizBuild.ts);
// students join from their own screens (routes/student/ClassGamePlay.tsx) and
// appear as the fish they made for the ocean. Answer fast for more points;
// streaks add a bonus. This page keeps the scores (lib/quiz.ts).
// ─────────────────────────────────────────────────────────────────────────────

type Player = { id: string; score: number; streak: number; answers: Map<number, { choice: number; secs: number; points: number }> };
type Phase = 'setup' | 'lobby' | 'question' | 'reveal' | 'leaderboard' | 'end';

const PRIZES = [10, 6, 3];

export default function ClassGame() {
  const { active, students, assignments, hydrated, dueFor } = useClassroom();
  const cid = active?.id;
  // The open work (sent out, not past due): what the questions come from.
  const week = useMemo(
    () => assignments.filter(a => isOpen(a, today(), students.map(s => dueFor(a, s.user_id)))),
    [assignments, students, dueFor],
  );
  const [decks, setDecks] = useState<Card[] | null>(null);
  const [looks, setLooks] = useState<Record<string, unknown>>({});
  const [use, setUse] = useState({ grammar: true, story: true, vocab: true });
  const [count, setCount] = useState(10);
  const [limit, setLimit] = useState(20);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [phase, setPhase] = useState<Phase>('setup');
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [i, setI] = useState(0);
  const [left, setLeft] = useState(0);
  const [, force] = useState(0);
  const game = useRef('');
  const players = useRef(new Map<string, Player>());
  const qStart = useRef(0);
  const counts = useRef<number[]>([]);
  const [awarded, setAwarded] = useState(false);

  const nameOf = useCallback((id: string) => students.find(s => s.user_id === id)?.display_name ?? 'A student', [students]);

  // Everyone's fish, and the class's decks (for the words they all saved).
  useEffect(() => {
    const ids = students.map(s => s.user_id);
    if (!ids.length) return;
    void supabase.from('student_avatars').select('student_id, look').in('student_id', ids)
      .then(({ data }) => setLooks(Object.fromEntries((data ?? []).map((r: { student_id: string; look: unknown }) => [r.student_id, r.look]))));
    fetchClassDecks(ids).then(d => setDecks(d.cards)).catch(() => setDecks([]));
  }, [students]);

  const grammar = useMemo(() => grammarQuestions(week), [week]);
  const phraseCount = useMemo(() => week.filter(a => a.kind === 'story' && a.story_text).flatMap(a => storyPhrases(a.story_text!)).length, [week]);
  const vocab = useMemo(() => (decks && cid ? sharedVocab(decks, weekStartOf(), cid) : { questions: [], students: 0 }), [decks, cid]);

  const link = useMemo(() => (cid ? quizLink(cid) : null), [cid]);

  // ── Sending the state ──
  const ranked = useCallback((): Ranked[] => [...players.current.values()]
    .map(p => ({ id: p.id, score: p.score, streak: p.streak, delta: 0 }))
    .sort((a, b) => b.score - a.score), []);

  const send = useCallback((s: QuizState) => { void link?.send('state', s); }, [link]);

  const stateNow = useCallback((): QuizState => {
    const q = questions[i];
    if (phase === 'lobby' || phase === 'setup') return { phase: 'lobby', game: game.current, title: active?.name ?? 'Class game' };
    if (phase === 'question' && q) {
      const { correct: _c, ...shown } = q; void _c;
      const answered = [...players.current.values()].filter(p => p.answers.has(i)).length;
      return { phase: 'question', game: game.current, i, total: questions.length, q: shown, limit, left, answered, players: players.current.size };
    }
    if (phase === 'reveal' && q) {
      const r = ranked();
      const results: Record<string, { right: boolean; points: number; score: number; rank: number; streak: number }> = {};
      r.forEach((p, k) => {
        const pl = players.current.get(p.id)!;
        const a = pl.answers.get(i);
        const right = !!a && a.choice === q.correct;
        results[p.id] = { right, points: a?.points ?? 0, score: p.score, rank: k + 1, streak: pl.streak };
      });
      return { phase: 'reveal', game: game.current, i, total: questions.length, q, counts: counts.current, results };
    }
    if (phase === 'leaderboard') return { phase: 'leaderboard', game: game.current, i, total: questions.length, top: ranked().slice(0, 5) };
    const r = ranked();
    return { phase: 'end', game: game.current, top: r.slice(0, 5), ranks: Object.fromEntries(r.map((p, k) => [p.id, { rank: k + 1, score: p.score }])) };
  }, [phase, questions, i, limit, left, ranked, active?.name]);
  const stateRef = useRef(stateNow); stateRef.current = stateNow;

  // Students joining and answering.
  useEffect(() => {
    if (!link) return;
    return link.on({
      join: id => {
        if (phase === 'setup' || !students.some(s => s.user_id === id)) return;
        if (!players.current.has(id)) { players.current.set(id, { id, score: 0, streak: 0, answers: new Map() }); quizTone('join'); force(n => n + 1); }
        send(stateRef.current());
      },
      answer: a => {
        if (a.game !== game.current || phase !== 'question' || a.i !== i) return;
        const p = players.current.get(a.id);
        if (!p || p.answers.has(i)) return;
        // Points use the seconds it took, as this page saw it.
        p.answers.set(i, { choice: a.choice, secs: (performance.now() - qStart.current) / 1000, points: 0 });
        force(n => n + 1);
      },
    });
  }, [link, phase, i, students, send]);

  // The lobby repeats itself now and then, for anyone who opens the page late.
  useEffect(() => {
    if (phase !== 'lobby') return;
    send(stateRef.current());
    const id = window.setInterval(() => send(stateRef.current()), 4000);
    return () => window.clearInterval(id);
  }, [phase, send]);

  // Leaving the page ends the game for everyone.
  useEffect(() => () => { if (game.current && link) void link.send('state', { phase: 'closed', game: game.current }); }, [link]);

  const reveal = useCallback(() => {
    const q = questions[i];
    if (!q) return;
    counts.current = q.options.map(() => 0);
    for (const p of players.current.values()) {
      const a = p.answers.get(i);
      if (a) counts.current[a.choice] = (counts.current[a.choice] ?? 0) + 1;
      if (a && a.choice === q.correct) {
        p.streak += 1;
        a.points = pointsFor(a.secs, limit, p.streak);
        p.score += a.points;
      } else p.streak = 0;
    }
    quizTone('reveal');
    setPhase('reveal');
  }, [questions, i, limit]);

  // The countdown: a second at a time, sending the time left; reveal at zero or when everyone has answered.
  useEffect(() => {
    if (phase !== 'question') return;
    send(stateRef.current());
    const id = window.setInterval(() => {
      const l = Math.max(0, limit - Math.floor((performance.now() - qStart.current) / 1000));
      setLeft(l);
      const everyone = players.current.size > 0 && [...players.current.values()].every(p => p.answers.has(i));
      if (l <= 5 && l > 0) quizTone('tick');
      if (l === 0 || everyone) { window.clearInterval(id); reveal(); }
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, i, limit, reveal, send]);

  // Every other change of screen goes out at once.
  useEffect(() => { if (phase === 'reveal' || phase === 'leaderboard' || phase === 'end') send(stateRef.current()); }, [phase, send]);
  // Answers coming in update everyone's "answered" count.
  useEffect(() => { if (phase === 'question') send(stateRef.current()); });

  const openLobby = async () => {
    setBuilding(true); setError(null);
    try {
      const lists: QuizQuestion[][] = [];
      if (use.grammar) lists.push(grammar);
      if (use.vocab) lists.push(vocab.questions);
      if (use.story && active) lists.push(await storyQuestions(week, active.language, active.native_language, count));
      const qs = mixQuestions(lists, count);
      if (!qs.length) { setError('No questions to play with these choices yet: assign a grammar lesson or a story, or pick another source.'); return; }
      setQuestions(qs);
      game.current = Math.random().toString(36).slice(2, 10);
      players.current = new Map();
      setI(0); setAwarded(false);
      setPhase('lobby');
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not make the questions.'); }
    finally { setBuilding(false); }
  };

  const ask = (k: number) => {
    setI(k);
    qStart.current = performance.now();
    setLeft(limit);
    quizTone('go');
    setPhase('question');
  };

  const next = () => {
    if (phase === 'reveal') { setPhase('leaderboard'); return; }
    if (i + 1 < questions.length) ask(i + 1);
    else { setPhase('end'); quizTone('fanfare'); burst(); }
  };

  const giveTop3 = async () => {
    if (!cid) return;
    const top = ranked().slice(0, 3);
    try {
      for (let k = 0; k < top.length; k++) await awardSandDollars(cid, [top[k].id], PRIZES[k], `Class game: ${['1st', '2nd', '3rd'][k]} place`);
      setAwarded(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not give the sand dollars.'); }
  };

  const endGame = () => {
    send({ phase: 'closed', game: game.current });
    game.current = '';
    setPhase('setup');
  };

  if (!hydrated) return <Loading />;
  if (!active) return <div className="notice">Create a class first.</div>;

  const q = questions[i];
  const board = ranked();
  const playerList = [...players.current.values()];

  // ── Setup ──
  if (phase === 'setup') return (
    <div className="narrow wide">
      <h1>🎮 Class game</h1>
      <p className="sub">A quick quiz the whole class plays live, from the work that's open now. Put this page on the projector (or share it in your meeting); students join on their own screens as their ocean fish.</p>
      <div className="panel game-setup">
        <h3>Questions from</h3>
        <label className="check-line"><input type="checkbox" checked={use.grammar} onChange={e => setUse(u => ({ ...u, grammar: e.target.checked }))} />
          <span>📝 Open grammar lessons <span className="small muted">({grammar.length} questions)</span></span></label>
        <label className="check-line"><input type="checkbox" checked={use.story} onChange={e => setUse(u => ({ ...u, story: e.target.checked }))} />
          <span>📖 Phrases from open stories: pick the translation <span className="small muted">({phraseCount} phrases)</span></span></label>
        <label className="check-line"><input type="checkbox" checked={use.vocab} onChange={e => setUse(u => ({ ...u, vocab: e.target.checked }))} />
          <span>🃏 Words every student saved this week <span className="small muted">
            ({decks == null ? 'checking…' : vocab.students < 2 ? 'fewer than two students saved words this week' : `${vocab.questions.length} word${vocab.questions.length === 1 ? '' : 's'} all ${vocab.students} saved`})</span></span></label>
        <div className="row" style={{ gap: 16, marginTop: 8 }}>
          <label className="small">Questions <input type="number" min={3} max={30} value={count} onChange={e => setCount(Math.max(3, Math.min(30, Math.round(Number(e.target.value) || 10))))} style={{ width: 70 }} /></label>
          <label className="small">Seconds each <select value={limit} onChange={e => setLimit(Number(e.target.value))}>{[10, 15, 20, 30, 45].map(s => <option key={s} value={s}>{s}</option>)}</select></label>
        </div>
        {error && <div className="notice bad small">{error}</div>}
        <button className="primary big" disabled={building || (!use.grammar && !use.story && !use.vocab)} onClick={() => void openLobby()}>{building ? 'Getting the questions ready…' : 'Open the game for students →'}</button>
      </div>
    </div>
  );

  // ── Live ──
  return (
    <div className="game-host">
      <div className="game-top">
        <strong>🎮 {active.name}</strong>
        {phase !== 'lobby' && phase !== 'end' && <span>Question {i + 1} of {questions.length}</span>}
        <span className="spacer" />
        <span>👥 {playerList.length}</span>
        <button className="small" onClick={endGame}>End game</button>
      </div>

      {phase === 'lobby' && (
        <div className="game-lobby">
          <h1>Join the class game!</h1>
          <p>Students: open <strong>Gibberfish Classroom</strong>. The game pops up on your screen: tap <strong>Join</strong>.</p>
          <div className="game-fish-tank">
            {playerList.length === 0 && <p className="muted">Waiting for players…</p>}
            {playerList.map(p => (
              <div key={p.id} className="game-player"><FishAvatar look={looks[p.id]} id={p.id} size={70} /><span>{nameOf(p.id)}</span></div>
            ))}
          </div>
          <button className="primary big" disabled={playerList.length === 0} onClick={() => ask(0)}>Start ({questions.length} questions) →</button>
        </div>
      )}

      {(phase === 'question' || phase === 'reveal') && q && (
        <div className="game-q">
          <div className="game-q-head">
            <div className="small muted">{q.instruction}</div>
            <div className="game-prompt" dir="auto">{q.prompt.split('___').map((part, k, arr) => <span key={k}>{part}{k < arr.length - 1 && <span className="blank">____</span>}</span>)}</div>
            {q.hint && <div className="muted">{q.hint}</div>}
          </div>
          {phase === 'question' && (
            <div className="game-status">
              <div className="game-timer" style={{ ['--p' as string]: left / limit }}><span>{left}</span></div>
              <div className="game-answered"><strong>{playerList.filter(p => p.answers.has(i)).length}</strong><span>answers</span></div>
            </div>
          )}
          {phase === 'reveal' && (
            <div className="game-bars">
              {q.options.map((_, k) => (
                <div key={k} className={`game-bar${k === q.correct ? ' right' : ''}`}>
                  <span className="n">{counts.current[k] ?? 0}</span>
                  <span className="b" style={{ height: `${10 + ((counts.current[k] ?? 0) / Math.max(1, playerList.length)) * 120}px`, background: TILES[k].color }} />
                  <span className="s" style={{ color: TILES[k].color }}>{TILES[k].shape}{k === q.correct ? ' ✓' : ''}</span>
                </div>
              ))}
            </div>
          )}
          <div className="game-tiles">
            {q.options.map((o, k) => (
              <div key={k} className={`game-tile${phase === 'reveal' ? (k === q.correct ? ' right' : ' dim') : ''}`} style={{ background: TILES[k].color }}>
                <span className="shape" aria-hidden>{TILES[k].shape}</span><span dir="auto">{o}</span>
              </div>
            ))}
          </div>
          {phase === 'reveal' && <div className="row" style={{ justifyContent: 'flex-end' }}><button className="primary big" onClick={next}>Next →</button></div>}
        </div>
      )}

      {phase === 'leaderboard' && (
        <div className="game-board">
          <h1>Leaderboard</h1>
          {board.slice(0, 5).map((p, k) => (
            <div key={p.id} className="game-rank">
              <span className="pos">{k + 1}</span>
              <FishAvatar look={looks[p.id]} id={p.id} size={46} />
              <strong className="grow">{nameOf(p.id)}</strong>
              {p.streak >= 2 && <span className="streak">🔥 {p.streak}</span>}
              <span className="pts">{p.score}</span>
            </div>
          ))}
          <div className="row" style={{ justifyContent: 'flex-end' }}><button className="primary big" onClick={next}>{i + 1 < questions.length ? 'Next question →' : 'And the winners are… →'}</button></div>
        </div>
      )}

      {phase === 'end' && (
        <div className="game-end">
          <h1>🏆 Champions</h1>
          <div className="podium">
            {[1, 0, 2].map(k => board[k] && (
              <div key={board[k].id} className={`podium-step p${k + 1}`}>
                <FishAvatar look={looks[board[k].id]} id={board[k].id} size={k === 0 ? 110 : 80} excited />
                <strong>{nameOf(board[k].id)}</strong>
                <span>{board[k].score} pts</span>
                <div className="step">{k + 1}</div>
              </div>
            ))}
          </div>
          <div className="row" style={{ justifyContent: 'center' }}>
            <button disabled={awarded || board.length === 0} onClick={() => void giveTop3()}>{awarded ? '🪙 Given!' : `🪙 Give the top 3 ${PRIZES.join(' / ')} sand dollars`}</button>
            <button className="primary" onClick={() => { setPhase('setup'); send({ phase: 'closed', game: game.current }); game.current = ''; }}>New game</button>
          </div>
          {error && <div className="notice bad small">{error}</div>}
        </div>
      )}
    </div>
  );
}
